import type { QueryClient } from '@tanstack/react-query';
import { clearSessionCsrfCookies } from '@aztec-realms/api-client';

import { browserApiClient, requiredApiData } from './browser-api';
import { deleteOfflineRealmDatabase } from './offline-realm';
import { unbindPushInstallation } from './push-installation';

const AUTHENTICATED_LOCAL_STORAGE_KEYS = [
  'aztec-realms:onboarding-guide-v1',
  'aztec-realms:onboarding-guide-v2',
  'aztec-realms:settlement-name-draft',
] as const;

// `aztec-realms:onboarding-complete` is deliberately absent: it is a device-level presentation
// preference, like reduced motion or interface scale. It contains no account or gameplay data and
// survives sign-out so the first-time guide is not forced on every new session. The guide remains
// replayable from Settings and Decode.

interface CacheStorageBoundary {
  keys(): Promise<string[]>;
  delete(cacheName: string): Promise<boolean>;
}

interface ServiceWorkerRegistrationBoundary {
  unregister(): Promise<boolean>;
}

interface ServiceWorkerBoundary {
  getRegistrations(): Promise<readonly ServiceWorkerRegistrationBoundary[]>;
}

export interface SessionTeardownDependencies {
  localStorage?: Pick<Storage, 'removeItem'>;
  sessionStorage?: Pick<Storage, 'clear'>;
  cacheStorage?: CacheStorageBoundary;
  serviceWorkers?: ServiceWorkerBoundary;
  clearOfflineState: () => Promise<void>;
  clearCsrfCookie: () => void;
  logoutFromServer: () => Promise<void>;
  unbindPush?: (online: boolean) => Promise<void>;
}

export interface SessionTeardownResult {
  serverSessionRevoked: boolean;
  offlineOnly: boolean;
}

function defaultDependencies(): SessionTeardownDependencies {
  return {
    localStorage: typeof window === 'undefined' ? undefined : window.localStorage,
    sessionStorage: typeof window === 'undefined' ? undefined : window.sessionStorage,
    cacheStorage: typeof caches === 'undefined' ? undefined : caches,
    serviceWorkers:
      typeof navigator === 'undefined' || !('serviceWorker' in navigator)
        ? undefined
        : navigator.serviceWorker,
    clearOfflineState: deleteOfflineRealmDatabase,
    clearCsrfCookie: clearSessionCsrfCookies,
    unbindPush: unbindPushInstallation,
    logoutFromServer: async () => {
      const response = await browserApiClient({ mutation: true }).POST('/api/v1/auth/logout');
      requiredApiData(response.data, response.error, 'The server could not confirm sign out.');
    },
  };
}

async function clearServiceWorkerState(
  cacheStorage: CacheStorageBoundary | undefined,
  serviceWorkers: ServiceWorkerBoundary | undefined,
): Promise<void> {
  const cacheNames = cacheStorage ? await cacheStorage.keys() : [];
  const registrations = serviceWorkers ? await serviceWorkers.getRegistrations() : [];
  await Promise.all([
    ...cacheNames.map(async (cacheName) => {
      await cacheStorage?.delete(cacheName);
    }),
    ...registrations.map(async (registration) => {
      await registration.unregister();
    }),
  ]);
}

/**
 * Tear down every client-held authenticated projection. Server logout is tried
 * only while online. Local cleanup never retains credentials or gameplay mutations;
 * explicit sign-out may retain bounded owner/device IDs solely for push revocation.
 * Session expiry preserves the push opt-in and public PWA shell registration.
 */
export async function terminateBrowserSession(
  queryClient: QueryClient,
  options: {
    online: boolean;
    preservePush?: boolean;
    dependencies?: SessionTeardownDependencies;
  },
): Promise<SessionTeardownResult> {
  const dependencies = options.dependencies ?? defaultDependencies();
  await queryClient.cancelQueries();

  let serverSessionRevoked = false;
  let logoutFailure: unknown;
  if (!options.preservePush) {
    // Revoke while the owner's session/CSRF token still exists. Offline cleanup queues only IDs.
    await dependencies.unbindPush?.(options.online).catch(() => undefined);
  }
  if (options.online) {
    try {
      await dependencies.logoutFromServer();
      serverSessionRevoked = true;
    } catch (error) {
      logoutFailure = error;
    }
  }

  queryClient.clear();
  dependencies.sessionStorage?.clear();
  for (const key of AUTHENTICATED_LOCAL_STORAGE_KEYS) {
    dependencies.localStorage?.removeItem(key);
  }
  dependencies.clearCsrfCookie();

  const cleanup = await Promise.allSettled([
    dependencies.clearOfflineState(),
    options.preservePush
      ? Promise.resolve()
      : clearServiceWorkerState(dependencies.cacheStorage, dependencies.serviceWorkers),
  ]);
  const cleanupFailures = cleanup.flatMap((result) =>
    result.status === 'rejected' ? [result.reason] : [],
  );
  if (logoutFailure || cleanupFailures.length > 0) {
    throw new AggregateError(
      [logoutFailure, ...cleanupFailures].filter((failure) => failure !== undefined),
      'The browser session could not be fully terminated.',
    );
  }
  return { serverSessionRevoked, offlineOnly: !options.online };
}
